import { useState } from "react";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared Mențiuni (notes/observations) section used in CasesPage and DosareBCPage.
 * 
 * Props:
 *   caseData - the selected case object (must have .id, .mentiuni)
 *   apiPath - API path for updating (e.g., "/api/cases" or "/api/cases-bc")
 *   getAuthHeader - function returning auth headers
 *   onUpdate - callback(updatedMentiuni) to update parent state
 *   isAdmin - whether current user is admin (controls add button visibility)
 */
export function MentiuniSection({ caseData, apiPath, getAuthHeader, onUpdate, isAdmin = true }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMentiune, setNewMentiune] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!newMentiune.trim()) {
      toast.error("Introduceți textul mențiunii");
      return;
    }

    setSubmitting(true);
    try {
      const today = format(new Date(), "dd/MM/yyyy");
      const existing = caseData.mentiuni || "";
      const newText = `[${today}] - ${newMentiune.trim()}`;
      const updated = existing ? `${existing}\n${newText}` : newText;

      await axios.put(
        `${API}${apiPath}/${caseData.id}`,
        { mentiuni: updated },
        getAuthHeader()
      );

      onUpdate(updated);
      setNewMentiune("");
      setDialogOpen(false);
      toast.success("Mențiune adăugată");
    } catch {
      toast.error("Eroare la adăugarea mențiunii");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="font-body text-muted-foreground">Mențiuni / Observații</Label>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setNewMentiune(""); setDialogOpen(true); }}
              data-testid="add-mentiune-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adaugă Mențiune
            </Button>
          )}
        </div>
        {caseData.mentiuni ? (
          <div className="p-3 bg-muted rounded-md whitespace-pre-wrap font-body text-sm">
            {caseData.mentiuni.replace(/\n{2,}/g, '\n').trim()}
          </div>
        ) : (
          <p className="text-center py-4 text-sm text-muted-foreground border rounded-md">
            Nu există mențiuni adăugate.
          </p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mentiune-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Adaugă Mențiune</DialogTitle>
            <DialogDescription className="font-body">
              Mențiunea va fi adăugată cu data de astăzi: {format(new Date(), "dd/MM/yyyy")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mentiune-text" className="font-body">Textul mențiunii</Label>
              <Textarea
                id="mentiune-text"
                value={newMentiune}
                onChange={(e) => setNewMentiune(e.target.value)}
                placeholder="Introduceți observația sau mențiunea..."
                rows={4}
                data-testid="input-mentiune"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
            <Button onClick={handleAdd} disabled={submitting} data-testid="submit-mentiune-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Adaugă"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
